// Pool-order API client. Shares the same Vite-dev / preview-host
// auth-header injection as the AOI client.

import { authHeaders } from '../../lib/authHeaders';

export interface PoolOrderListItem {
  id: string;
  docket_number: string | null;
  cause_number: string | null;
  application_type: string | null;
  operator: string | null;
  filed_date: string | null;
  hearing_date: string | null;
  county: string | null;
  parse_status: string;
  created_at: string;
  names_total: number;
  names_matched: number;
  names_suggested: number;
  names_unmatched: number;
  trs_keys: string[];
}

export interface PoolOrderTrs {
  township_num: number;
  township_dir: 'N' | 'S';
  range_num: number;
  range_dir: 'E' | 'W';
  section: number;
}

export interface PoolOrderMeta {
  id: string;
  docket_number: string | null;
  cause_number: string | null;
  application_type: string | null;
  operator: string | null;
  operator_number: string | null;
  county: string | null;
  order_number: string | null;
  filed_date: string | null;
  hearing_date: string | null;
  formations: string[];
  wellbore_apis: Array<{ name: string; api: string }>;
  referenced_order_numbers: string[];
  pdf_application_r2_key: string | null;
  pdf_names_r2_key: string | null;
  parse_status: string;
  parse_warnings: string[];
  notes: string | null;
  // NULL when the corresponding Vision call has never completed (no PDF
  // uploaded, or upload-time call crashed and no re-fire has succeeded).
  // Non-NULL even when the call returned an empty array — Vision saying
  // "no table" is a valid completion. See migration 017.
  appendix_a_extracted_at: string | null;
  names_extracted_at: string | null;
  /** True when a pasted map image is on file for this docket.
   *  Flips to false when DELETE clears it. */
  has_map_image: boolean;
  /** Timestamp the current map image was last uploaded. NULL when
   *  no image. Drives the cache-buster on the <img src>. */
  map_image_uploaded_at: string | null;
  created_at: string;
  trs: PoolOrderTrs[];
}

export interface RosterEntry {
  pool_order_name_id: string;
  name_as_appears: string;
  bucket: 'matched' | 'suggested' | 'watch_list';
  party_id: string | null;
  matched_display_name: string | null;
  matched_entity_type: string | null;
  match_confidence: number | null;
  matched_last_recording_date: string | null;
  suggested_party_id: string | null;
  suggested_display_name: string | null;
  suggested_entity_type: string | null;
  suggested_match_confidence: number | null;
  investment_score: number | null;
  investment_reasoning: string | null;
  parse_warnings: string[];
  has_phone?: boolean;
  has_address?: boolean;
  last_pushed_to_close_at: string | null;
  skipped_at: string | null;
  lead_list_status: 'pending' | 'researched' | null;
  /** True when this party appears as grantor on a mineral or royalty
   *  deed whose TRS overlaps THIS pool order's TRS — same shape as
   *  the AOI roster's has_sold_minerals, scoped to the docket. */
  has_sold_minerals: boolean;
  // Force-pool enrichment, computed server-side for every matched +
  // suggested row. Null on watch_list rows (no party to enrich).
  oldest_activity: string | null;
  has_recent_acquisition: boolean;
  most_recent_lease_kind: 'lease' | 'release' | null;
  most_recent_lease_date: string | null;
}

export interface CoLessorEntry {
  party_id: string;
  display_name_natural: string;
  match_confidence: number;
  matched_token: string;
}

export interface LeasedOwnerEntry {
  id: string;
  reception_number: string | null;
  lessor_name: string;
  lessee_name: string | null;
  lease_date: string | null;
  county: string | null;
  township_num: number | null;
  township_dir: 'N' | 'S' | null;
  range_num: number | null;
  range_dir: 'E' | 'W' | null;
  section: number | null;
  tract_description: string | null;
  matched_doc_number: string | null;
  matched_doc_recording_date: string | null;
  matched_doc_type: string | null;
  party_id: string | null;
  match_confidence: number | null;
  lessor_party_display_name: string | null;
  lessor_party_display_name_natural: string | null;
  suggested_party_id: string | null;
  suggested_confidence: number | null;
  suggested_lessor_display_name: string | null;
  suggested_lessor_display_name_natural: string | null;
  lessee_party_id: string | null;
  lessee_confidence: number | null;
  lessee_party_display_name: string | null;
  lessee_party_display_name_natural: string | null;
  parse_warnings: string[];
  co_lessor_parties: CoLessorEntry[];
}

export interface AnalysisPick {
  party_id: string;
  display_name_natural: string;
  score: number;
  reason: string;
}

export interface AnalysisRiskFlag {
  kind: 'operator_new_entrant' | 'appendix_a_incomplete' | 'formation_unusual' | 'hearing_soon' | 'data_quality' | 'other';
  detail: string;
}

export interface AnalysisComparable {
  docket_number: string;
  operator: string;
  filed_date: string | null;
  why_comparable: string;
}

export interface PoolOrderAnalysis {
  thesis_paragraph: string;
  urgent_outreach_top_3: AnalysisPick[];
  lower_priority_top_3: AnalysisPick[];
  risk_flags: AnalysisRiskFlag[];
  comparable_recent_dockets: AnalysisComparable[];
  operator_pattern_note: string;
  notes: string;
}

export interface PoolOrderAnalysisMeta {
  generated_at: string;
  cost_usd: number | null;
  model: string | null;
  prompt_version: string | null;
}

export interface PoolOrderDetailResponse {
  pool_order: PoolOrderMeta;
  roster: RosterEntry[];
  leased_owners: LeasedOwnerEntry[];
  claude_analysis: { analysis: PoolOrderAnalysis; meta: PoolOrderAnalysisMeta } | null;
  counts: {
    all: number;
    matched: number;
    suggested: number;
    watch_list: number;
    watch_list_scored: number;
    watch_list_unscored: number;
    leased_owners: number;
    leased_owners_doc_matched: number;
    leased_owners_lessor_matched: number;
    leased_owners_lessee_matched: number;
    leased_owners_unknown: number;
    force_pool: number;
  };
  operator_avg_primary_term_months: number | null;
}

export interface ScoreUnmatchedResponse {
  pool_order_id: string;
  status: 'complete' | 'noop';
  scored_count: number;
  skipped_count?: number;
  scored?: Array<{ pool_order_name_id: string; name_as_appears: string; score: number; reasoning: string }>;
}

async function jsonReq<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json() as Promise<T>;
}

export const poolOrderApi = {
  list: () => jsonReq<{ pool_orders: PoolOrderListItem[] }>('/api/mi/pool-orders'),
  get:  (id: string) => jsonReq<PoolOrderDetailResponse>(`/api/mi/pool-orders/${id}`),

  upload: async (form: FormData) => {
    const r = await fetch('/api/mi/ingest/pool-order', {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: form,
    });
    if (!r.ok) {
      let detail = r.statusText;
      try { detail = (await r.json()).error ?? detail; } catch {}
      throw new Error(`${r.status} ${detail}`);
    }
    return r.json() as Promise<{ pool_order_id: string; docket_number: string | null; parse_status: 'complete' | 'failed'; parse_warnings: string[] }>;
  },

  scoreUnmatched: (id: string) =>
    jsonReq<ScoreUnmatchedResponse>(`/api/mi/pool-orders/${id}/score-unmatched`, { method: 'POST' }),

  retryParse: (id: string) =>
    jsonReq<{ pool_order_id: string; parse_status: 'complete' | 'failed'; parse_warnings: string[] }>(`/api/mi/pool-orders/${id}/retry-parse`, { method: 'POST' }),

  delete: (id: string) =>
    jsonReq<{ ok: true }>(`/api/mi/pool-orders/${id}`, { method: 'DELETE' }),

  // Map image paste — same shape as aoiApi.uploadMapImage. The body
  // is the raw image blob; Content-Type tells the server which
  // extension to use for the R2 key.
  uploadMapImage: async (id: string, blob: Blob): Promise<{ ok: true; bytes: number }> => {
    const r = await fetch(`/api/mi/pool-orders/${id}/map-image`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders(), 'Content-Type': blob.type || 'image/png' },
      body: blob,
    });
    if (!r.ok) {
      let detail = r.statusText;
      try { detail = (await r.json()).error ?? detail; } catch { /* fallthrough */ }
      throw new Error(`${r.status} ${detail}`);
    }
    return r.json();
  },
  deleteMapImage: (id: string) =>
    jsonReq<{ ok: true }>(`/api/mi/pool-orders/${id}/map-image`, { method: 'DELETE' }),

  /** Per-party documents scoped to the pool order's TRS. Mirrors the
   *  AOI variant — same shape, same lazy-load pattern on the row
   *  expansion chevron. */
  partyDocs: (poolOrderId: string, partyId: string) =>
    jsonReq<{ documents: Array<{
      doc_number: string;
      ordinal: number;
      doc_type: string | null;
      recording_date: string | null;
      effective_date: string | null;
      primary_term_months: number | null;
      royalty_pct: string | null;
      gross_acreage: string | null;
      lessee: string | null;
      trs_label: string | null;
    }> }>(`/api/mi/pool-orders/${poolOrderId}/parties/${partyId}/docs`),

  extractNames: (id: string) =>
    jsonReq<{
      pool_order_id: string;
      names_total: number;
      names_matched_exact: number;
      names_matched_trigram_high: number;
      names_with_trigram_suggestion: number;
      names_unmatched_pure_watch: number;
    }>(`/api/mi/pool-orders/${id}/extract-names`, { method: 'POST' }),

  extractAppendixA: (id: string) =>
    jsonReq<{
      pool_order_id: string;
      leased_owners_total: number;
      leased_owners_doc_matched: number;
      leased_owners_lessor_matched: number;
      leased_owners_lessee_matched: number;
    }>(`/api/mi/pool-orders/${id}/extract-appendix-a`, { method: 'POST' }),

  analyze: (id: string) =>
    jsonReq<{
      pool_order_id: string;
      analysis: PoolOrderAnalysis;
      meta: PoolOrderAnalysisMeta & { input_tokens: number; output_tokens: number; stop_reason: string | null };
      bundle_summary: Record<string, number>;
    }>(`/api/mi/pool-orders/${id}/analyze`, { method: 'POST' }),

  pdfUrl: (id: string, which: 'application' | 'names') =>
    `/api/mi/pool-orders/${id}/pdf?which=${which}`,

  promote: (poolOrderNameId: string) =>
    jsonReq<{ pool_order_name_id: string; party_id: string; created_party: boolean; matched_existing_party?: boolean; status?: string }>(
      `/api/mi/pool-order-names/${poolOrderNameId}/promote`, { method: 'POST' }
    ),
  confirmSuggestion: (poolOrderNameId: string) =>
    jsonReq<{ pool_order_name_id: string; party_id: string; match_confidence: number; status: string }>(
      `/api/mi/pool-order-names/${poolOrderNameId}/confirm-suggestion`, { method: 'POST' }
    ),
  rejectSuggestion: (poolOrderNameId: string) =>
    jsonReq<{ pool_order_name_id: string; status: string }>(
      `/api/mi/pool-order-names/${poolOrderNameId}/reject-suggestion`, { method: 'POST' }
    ),
};
